import PropTypes from "prop-types";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Modal from "../../../../../parts/modal-new";
import SvgIcon from "../../../../../parts/SvgIcon";
import {
  applyRecipeIngredientEdits,
  customRound,
  getFilteredFoodsForAddModal,
  getMealLocationForAddModal,
  getMealPlanRecipeIngredients,
  getRecipeRowMacros,
  isRecipeType,
  parseFoodData,
  reCalculateMacros,
} from "../../../../../utils/nutrition-util";
import { simpleHash } from "../../../../../utils/string-helpers";
import { FoodActions } from "../modal-components/FoodActions";
import { FoodDisplay } from "../modal-components/FoodDisplay";
import { RecentFoods } from "../modal-components/RecentFoods";

/**
 * Ingredient edits of one recipe as the list the API expects
 * @param {{ [ingredientIndex: string]: number }} [edits]
 * @returns {{ ingredientIndex: number, amount: number }[]}
 */
const toIngredientEditList = (edits = {}) =>
  Object.entries(edits).map(([ingredientIndex, amount]) => ({
    ingredientIndex: Number(ingredientIndex),
    amount,
  }));

import { EditRecipeIngredientsModal } from "./EditRecipeIngredientsModal";

export const AddFoodModal = ({ locals, api }) => {
  const { t } = useTranslation("coaching-portal");
  const { mostUsedMealPlans, mealPlan, query } = locals;
  const { weeks = [] } = mealPlan;
  const planMode = mealPlan?.planMode || "detailed";
  const {
    week = 1,
    mealPlannerDate,
    mealPlannerMeal,
    athlete,
    dayType,
    optionId,
  } = query;

  // For simplified, get foods from the specific option
  // For detailed, use existing logic
  let filteredFoodsOfMeal = [];
  // Position of the meal in the stored plan (detailed only), used to load recipe ingredients
  let mealLocation = null;
  if (planMode === "simplified" && dayType && optionId) {
    const optionsArray =
      dayType === "training"
        ? mealPlan?.trainingDayOptions || []
        : mealPlan?.restDayOptions || [];
    const option = optionsArray.find(
      (opt) => opt.optionId?.toString() === optionId?.toString()
    );
    const meal = option?.meals?.find((m) => m.type === mealPlannerMeal);
    // Use parseFoodData to transform foods to the expected format (same as detailed)
    filteredFoodsOfMeal = parseFoodData(meal?.foods || []);
  } else {
    const selectedWeek = weeks[week - 1]?.days || [];
    filteredFoodsOfMeal = getFilteredFoodsForAddModal(
      selectedWeek,
      mealPlannerDate,
      mealPlannerMeal
    );
    const { dayIndex, mealIndex } = getMealLocationForAddModal(
      selectedWeek,
      mealPlannerDate,
      mealPlannerMeal
    );
    if (mealIndex >= 0) {
      mealLocation = { weekIndex: week - 1, dayIndex, mealIndex };
    }
  }

  const initialState = {
    searchFilter: "all",
    addedItems: filteredFoodsOfMeal,
  };
  const initialFoodDisplayState = {
    saveAsNewMeal: false,
    mealName: "",
    mealDescription: "",
  };

  const initialFoodActionState = {
    selectedItem: null,
    unitOfMeasure: "Select",
    unitOfMeasureOptions: [],
    isSearching: false,
    searchValue: "",
  };

  const [foodDisplayState, setFoodDisplayState] = useState(
    initialFoodDisplayState
  );
  const foodDisplayRef = useRef(null);
  const foodActionsRef = useRef(null);
  const [state, setState] = useState(initialState);
  const [foodActionState, setFoodActionState] = useState(
    initialFoodActionState
  );
  // Ingredients of the stored external recipes, keyed by their index in the stored meal.foods
  // { [foodIndex]: { status: "loading" | "loaded" | "error", food, ingredientDetails, errorStatus } }
  const [recipeIngredients, setRecipeIngredients] = useState({});
  // foodIndex of the recipe whose ingredients modal is open (opens over this modal)
  const [editingIngredientsFoodIndex, setEditingIngredientsFoodIndex] =
    useState(null);
  // Ingredient edits kept locally until Save, sent with the PUT
  // { [foodIndex]: { [ingredientIndex]: whole-recipe amount } }
  const [ingredientEdits, setIngredientEdits] = useState({});
  const editingIngredientsBaseFood =
    editingIngredientsFoodIndex !== null
      ? recipeIngredients[editingIngredientsFoodIndex]?.food
      : null;
  // The modal shows the recipe with the pending edits applied
  const editingIngredientsFood = editingIngredientsBaseFood && {
    ...editingIngredientsBaseFood,
    recipe: applyRecipeIngredientEdits(
      editingIngredientsBaseFood.recipe,
      toIngredientEditList(ingredientEdits[editingIngredientsFoodIndex])
    ),
  };
  const { saveAsNewMeal } = foodDisplayState;

  const { searchFilter, addedItems } = state;
  // Quantity currently set on the row (may be changed but not saved yet)
  const editingIngredientsQuantity = addedItems.find(
    ({ foodIndex }) => foodIndex === editingIngredientsFoodIndex
  )?.customQuantityNumber;
  const mostUsedToUse = mostUsedMealPlans[searchFilter];

  const handleExternalItemSelect = (item) => {
    setTimeout(() => {
      foodActionsRef.current?.focusQuantityInput();
    }, 100);
  };

  const mealLocationKey = mealLocation
    ? `${mealPlan._id}-${mealLocation.weekIndex}-${mealLocation.dayIndex}-${mealLocation.mealIndex}`
    : "";

  // Load the ingredients of every stored external recipe when the modal opens
  // biome-ignore lint/correctness/useExhaustiveDependencies: mealLocation and filteredFoodsOfMeal are rebuilt every render; mealLocationKey identifies the opened meal
  useEffect(() => {
    if (!mealLocation) return;
    let cancelled = false;
    const setEntry = (foodIndex, entry) => {
      if (cancelled) return;
      setRecipeIngredients((prevState) => ({
        ...prevState,
        [foodIndex]: entry,
      }));
    };

    filteredFoodsOfMeal
      .filter(({ type }) => type === "external_recipe")
      .forEach(({ foodIndex }) => {
        setEntry(foodIndex, { status: "loading" });
        getMealPlanRecipeIngredients(
          mealPlan._id,
          { ...mealLocation, foodIndex },
          athlete
        )
          .then(({ food, ingredientDetails }) =>
            setEntry(foodIndex, { status: "loaded", food, ingredientDetails })
          )
          .catch((error) =>
            setEntry(foodIndex, { status: "error", errorStatus: error?.status })
          );
      });

    return () => {
      cancelled = true;
    };
  }, [mealLocationKey]);

  // UseEffect to know when saveAsNewMeal is true and focus to the input
  useEffect(() => {
    if (saveAsNewMeal) {
      setTimeout(() => {
        foodDisplayRef.current?.focusMealName();
      }, 100);
    }
  }, [saveAsNewMeal]);

  /**
   * Function to update the state of the component
   * @param {*} newState
   * @returns
   */
  const updateState = (newState) =>
    setState((prevState) => ({ ...prevState, ...newState }));

  /**
   * open create-new-food modal or data-source modal
   */
  const handleOpenModal = (destination) => {
    const shouldConfirm =
      addedItems?.length > 0 && filteredFoodsOfMeal?.length === 0;

    if (
      !shouldConfirm ||
      (process.browser && confirm(t("editMealPlan.addFoodModal.leaveConfirm")))
    ) {
      submit(`/actions/refresh/?`, {
        method: "GET",
        body: {
          "modal[type]": destination,
        },
      });
    }
  };

  /**
   * Insert foods in the day
   */
  const handleCreateMeal = () => {
    if (mealPlannerDate && mealPlannerMeal) {
      const { mealName, mealDescription } = foodDisplayState;
      const isNewMeal = saveAsNewMeal && !!mealName.trim();
      const foodsWithMealFormat = addedItems.map((item) => {
        const {
          type,
          _id,
          customQuantityNumber,
          customUnitOfMeasure,
          mealId,
          foodIndex,
          ...rest
        } = item;
        const isStoredExternalRecipe =
          type === "external_recipe" && foodIndex != null;
        const editList = isStoredExternalRecipe
          ? toIngredientEditList(ingredientEdits[foodIndex])
          : [];

        let foodIdToUse = _id;

        if (type === "public_recipe" && !_id) {
          foodIdToUse = rest.recipeId || rest.id || rest._id || null;

          // Debug log to see the structure
          if (process.browser) {
            console.log("Public recipe without _id:", {
              type,
              itemKeys: Object.keys(item),
              _id,
              recipeId: rest.recipeId,
              id: rest.id,
              fullItem: item,
            });
          }
        }

        if (!foodIdToUse && process.browser) {
          console.warn("Food item missing foodId:", {
            type,
            item,
          });
        }

        return {
          type: type || "food",
          foodId: foodIdToUse,
          quantityNumber: customQuantityNumber || 1,
          unitOfMeasure: customUnitOfMeasure,
          ...(mealId ? { mealId } : {}),
          // Lets the API find the stored recipe copy to keep and apply the edits to
          ...(isStoredExternalRecipe ? { originalFoodIndex: foodIndex } : {}),
          ...(editList.length > 0 ? { ingredientEdits: editList } : {}),
        };
      });
      const { _id: mealPlanId } = mealPlan;

      // Common body properties
      const commonBody = {
        mealType: mealPlannerMeal,
        foods: foodsWithMealFormat,
        bodyAction: "handle-food",
        handle: "add",
        isTemplate: athlete ? false : true,
      };

      // Build body based on planMode
      let body = {};
      if (planMode === "simplified" && dayType && optionId) {
        // Simplified meal plan: use dayType and optionId
        body = {
          ...commonBody,
          planMode: "simplified",
          dayType: dayType, // "training" or "rest"
          optionId: optionId, // Stable ID of the option
        };
      } else {
        // Detailed meal plan: use weekIndex and date
        const weekIndex = week - 1;
        body = {
          ...commonBody,
          weekIndex,
          date: mealPlannerDate,
        };
      }

      let url = `/actions/meal-planner/update-days-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`;

      if (isNewMeal) {
        body = {
          ...body,
          name: mealName,
          description: mealDescription.trim() ? mealDescription : "description",
        };
        url = `/actions/meal-planner/update-days-meal-plan-and-add-meal?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`;
      }

      const requestId = simpleHash(JSON.stringify(body));

      body = {
        ...body,
        requestId,
      };

      submit(url, {
        method: "POST",
        body,
      });
    }
  };

  /**
   * Callback to handle the updates of the item (quantity)
   */
  const handleUpdateItems = useCallback(
    (index, updatedItem) => {
      updateState({
        addedItems: addedItems.map((item, i) =>
          i === index
            ? {
                ...item,
                ...updatedItem,
                // When quantity is updated, allow recalculation of macros
                // This ensures both FoodItems (nutrition-info) and totalOfNutritionalValues recalculate
                skipRecalculation:
                  updatedItem.customQuantityNumber !== undefined
                    ? false
                    : item.skipRecalculation,
              }
            : item
        ),
      });
    },
    [addedItems]
  );

  /**
   * Function to add an item to the display component
   * @param {*} item
   */

  const handleAddItem = useCallback(
    (items) => {
      updateState({ addedItems: [...addedItems, ...items] });
    },
    [addedItems]
  );

  /**
   * Keep the ingredient edits of the open recipe locally (sent on Save) and recalculate
   * its row macros, so the row and the totals use the edited recipe from now on
   * @param {{ ingredientIndex: number, amount: number }[]} edits whole-recipe amounts
   */
  const handleSaveIngredientEdits = (edits) => {
    const foodIndex = editingIngredientsFoodIndex;
    const mergedEdits = {
      ...ingredientEdits[foodIndex],
      ...Object.fromEntries(
        edits.map(({ ingredientIndex, amount }) => [ingredientIndex, amount])
      ),
    };
    const editedRecipe = applyRecipeIngredientEdits(
      recipeIngredients[foodIndex].food.recipe,
      toIngredientEditList(mergedEdits)
    );

    setIngredientEdits((prevState) => ({
      ...prevState,
      [foodIndex]: mergedEdits,
    }));
    // Row macros are kept scaled to macrosScaledToQuantity (see parseFoodData), so
    // quantity changes keep re-portioning them from the edited recipe
    updateState({
      addedItems: addedItems.map((item) =>
        item.foodIndex === foodIndex
          ? {
              ...item,
              ...getRecipeRowMacros(editedRecipe, item.macrosScaledToQuantity),
            }
          : item
      ),
    });
    setEditingIngredientsFoodIndex(null);
  };

  /**
   * Function to remove an item of the display component
   * @param {*} index
   */

  const handleRemoveItem = useCallback(
    (index) => {
      const updatedItems = [...addedItems];
      updatedItems.splice(index, 1);
      updateState({ addedItems: updatedItems });
    },
    [addedItems]
  );

  /**
   * Function to change the option in the tabs
   * @param {*} filter
   */
  const handleChangeTab = (filter) => {
    updateState({ searchFilter: filter });
    setFoodActionState(initialFoodActionState);
  };

  /**
   * Calculate the total of the Nutritional values
   */
  const totalOfNutritionalValues = useMemo(() => {
    return addedItems.reduce(
      (acc, item) => {
        const updatedValues = reCalculateMacros({
          itemDetails: {
            itemProteins: item.proteins || 0,
            itemCarbs: item.carbs || 0,
            itemFats: item.fats || 0,
            itemCalories: item.calories || 0,
            quantityNumber: item.quantityNumber || 1,
            customQuantityNumber: item.customQuantityNumber || 1,
            measurementsValue: item.measurementsValue || 1,
            customUnitOfMeasure: item.customUnitOfMeasure || "custom",
            type: item.type,
            skipRecalculation: item.skipRecalculation,
            macrosScaledToQuantity: item.macrosScaledToQuantity,
            ...(isRecipeType(item?.type) && {
              recipeMacrosBase: item?.quantityDescription,
            }),
          },
        });

        acc.totalProteins += updatedValues.proteins;
        acc.totalCarbs += updatedValues.carbs;
        acc.totalFats += updatedValues.fats;
        acc.totalCalories += updatedValues.calories;

        return acc;
      },
      { totalCalories: 0, totalCarbs: 0, totalProteins: 0, totalFats: 0 }
    );
  }, [addedItems]);

  const { totalCalories, totalCarbs, totalProteins, totalFats } =
    totalOfNutritionalValues;

  return (
    <Modal
      className={`add-food-modal`}
      title={t("editMealPlan.addFoodModal.title")}
      toClear={["dayType", "optionId", "mealPlannerDate", "mealPlannerMeal"]}
    >
      <div className="body">
        <div className="header-buttons">
          <div className="first-row">
            <nav role="tablist">
              <div className="buttons-options">
                {["all", "recipes", "meals", "foods"].map((filter) => (
                  <a
                    key={filter}
                    id={filter}
                    onClick={() => handleChangeTab(filter)}
                    aria-current={searchFilter === filter ? true : undefined}
                  >
                    <span>{t(`editMealPlan.addFoodModal.tabs.${filter}`)}</span>
                  </a>
                ))}
              </div>
              <hgroup className="create-new">
                <button
                  data-button-type={"outline"}
                  onClick={() => handleOpenModal("create-new-food")}
                >
                  <span>{t("editMealPlan.addFoodModal.createNew")}</span>
                </button>
              </hgroup>
            </nav>
          </div>
        </div>
        <div className="main-container">
          <div className="add-food-main-container">
            <FoodActions
              ref={foodActionsRef}
              locals={locals}
              searchFilter={searchFilter}
              api={api}
              onAddItem={handleAddItem}
              foodActionState={foodActionState}
              setFoodActionState={setFoodActionState}
            />
            <div className="added-text">
              <span className="clipped font-semibold h-5">
                {t("editMealPlan.addFoodModal.added")}
              </span>
            </div>

            <FoodDisplay
              ref={foodDisplayRef}
              state={foodDisplayState}
              setState={setFoodDisplayState}
              items={addedItems}
              location={searchFilter}
              onRemoveItem={handleRemoveItem}
              onUpdateItems={handleUpdateItems}
              recipeIngredients={recipeIngredients}
              recipeIngredientsSupported={Boolean(mealLocation)}
              onEditIngredients={setEditingIngredientsFoodIndex}
            />
          </div>
          <div className="recent-foods-main-container">
            <RecentFoods
              mostUsedToUse={mostUsedToUse}
              location={searchFilter}
              setFoodActionState={setFoodActionState}
              onItemSelect={handleExternalItemSelect}
            />
          </div>
        </div>

        <div className="footer">
          <div className="data-source">
            <button
              data-button-type="secondary"
              onClick={() => handleOpenModal("data-source")}
            >
              <SvgIcon src="/img/new-icons/settings.svg" alt="" />
              <span>{t("editMealPlan.addFoodModal.dataSource")}</span>
            </button>
            {addedItems.length > 0 && searchFilter !== "meals" && (
              <div className="checkbox-container">
                <SvgIcon
                  noFilterOnLight={saveAsNewMeal}
                  onClick={() => {
                    setFoodDisplayState((prevState) => ({
                      ...prevState,
                      saveAsNewMeal: !prevState.saveAsNewMeal,
                    }));
                  }}
                  src={`/img/new-icons/${
                    saveAsNewMeal
                      ? "checked-square.svg"
                      : "unchecked-square.svg"
                  }`}
                />
                <span>{t("editMealPlan.addFoodModal.saveAsNewMeal")}</span>
              </div>
            )}
          </div>
          <div className="nutrition-button-container">
            <div className="footer-nutrition">
              <div>
                <span className="mr-2 kcal">
                  {t("editMealPlan.addFoodModal.kCal")}
                </span>
                <span>{customRound(totalCalories)}</span>
              </div>
              <div>
                <span className="mr-2 protein">
                  {t("editMealPlan.addFoodModal.protein")}
                </span>
                <span>{customRound(totalProteins)}g</span>
              </div>
              <div>
                <span className="mr-2 carbs">
                  {t("editMealPlan.addFoodModal.carbs")}
                </span>
                <span>{customRound(totalCarbs)}g</span>
              </div>
              <div>
                <span className="mr-2 fat">
                  {t("editMealPlan.addFoodModal.fat")}
                </span>
                <span>{customRound(totalFats)}g</span>
              </div>
            </div>
            <div className="button-modal">
              <a
                role="button"
                data-button-type="secondary"
                data-action-cancel
                className="button alt"
                href={`/actions/refresh?modal=`}
              >
                {t("editMealPlan.addFoodModal.cancel")}
              </a>
              <button
                data-button-type="primary"
                onClick={handleCreateMeal}
                disabled={
                  addedItems.length === 0 ||
                  addedItems.some(
                    ({ customQuantityNumber }) =>
                      customQuantityNumber === "" || isNaN(customQuantityNumber)
                  )
                }
              >
                {t("editMealPlan.addFoodModal.save")}
              </button>
            </div>
          </div>
        </div>
      </div>
      {editingIngredientsFood && (
        <EditRecipeIngredientsModal
          food={editingIngredientsFood}
          quantity={editingIngredientsQuantity}
          onSave={handleSaveIngredientEdits}
          onClose={() => setEditingIngredientsFoodIndex(null)}
        />
      )}
    </Modal>
  );
};

AddFoodModal.propTypes = {
  locals: PropTypes.object,
  api: PropTypes.any,
};
